/**
 * TerraformAdjustCard — cartão de CONFIRMAÇÃO de um ajuste de infraestrutura
 * proposto via Terraform (runner de Terraform, spec 2026-09-04).
 *
 * Este é o portão 2 do fluxo (o portão 1 é a permissão estrita
 * `terraform:adjust` + role `devops`, avaliada no servidor antes de o pedido
 * chegar aqui — spec §7.1/§7.2, `terraform_runner/routes.py`). O que este
 * card mostra é o que vai mudar (`diff`), o custo (com a ressalva que o
 * viaja em TODO retorno, sucesso ou falha), os avisos (parada de máquina) e,
 * quando o knob é irreversível, exige repetir o valor-alvo antes de habilitar
 * "Confirmar" — o atrito é proporcional ao que está em jogo, nunca decorativo.
 *
 * NÃO é componente paralelo: mesmo esqueleto de `TicketHandoffCard` e
 * `ChamadoCommentApplyCard` (`Card` › `Group` › `Text`/`Badge` › `Button`),
 * despachado por `ActionProposalCard` como mais um `action_type`.
 *
 * Regra de negócio ("nenhum número que não mude decisão", spec §10.4): um
 * delta de custo (`custo.deltaMensal`) NUNCA aparece sozinho — sem número
 * (`deltaMensal == null`) o card mostra só a ressalva, nunca "US$ —" ou
 * qualquer variante que pareça um valor.
 *
 * `tipo_instancia` (campo opcional do backend que alimenta `pricing.estimar`)
 * não é um prop deste card: quem manda esse campo é o chamador da rota
 * `/change-requests`, e este componente só CONSOME o resultado já calculado
 * (`custo`). Quando o backend não recebeu `tipo_instancia`, `custo.ressalva`
 * já chega como `SEM_PRECO` e `deltaMensal` como `null` — o mesmo caminho do
 * teste "sem ressalva o número não aparece sozinho".
 */
import { useState } from 'react'
import { Anchor, Badge, Button, Card, Group, Stack, Text, TextInput } from '@mantine/core'
import { IconAdjustments } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import type { PedidoDoCartao } from '@/api/terraformRunner'
import { Dado } from './Dado'
import { DecisaoNoCartao } from './DecisaoNoCartao'
import { fraseDoEncobre } from '@/components/chamados/DecisaoDoPedido'
import { LinhaDoTempoDoPedido } from './LinhaDoTempoDoPedido'
import { MotivoDoPedido } from './MotivoDoPedido'
import { PausaDaFila } from './PausaDaFila'
import { PainelDeExecucao, totalDoPlan } from './PainelDeExecucao'

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface TerraformDiffItem {
  campo: string
  de: unknown
  para: unknown
}

export interface TerraformCusto {
  deMensal: number | null
  paraMensal: number | null
  deltaMensal: number | null
  moeda: string
  /** Viaja com o número em TODO caso, sucesso ou falha — nunca omitida. */
  ressalva?: string
}

export type TerraformAdjustCardStatus = 'pending' | 'executed' | 'failed' | 'declined'

export interface TerraformAdjustCardProps {
  stageChave: string
  knob: string
  alvo: string
  diff: TerraformDiffItem[]
  custo: TerraformCusto
  avisos: string[]
  irreversivel: boolean
  exigeRepetirValor: boolean
  status?: TerraformAdjustCardStatus
  /** Gate de permissão — calculado pelo mesmo mecanismo de `ACTION_GATES`
   * do despachante (papel `devops` + `terraform:adjust`). Default `true`
   * para não quebrar quem instancia o card sem esse dado (ex.: este teste). */
  canConfirmPermission?: boolean
  /** `true` quando o backend NÃO informou se o knob é irreversível
   * (`exige_repetir_valor` ausente do payload — proposta sem o
   * enriquecimento de dry-run, ou versão antiga). Fail-closed: NUNCA lido
   * como "não é irreversível", mesmo que `irreversivel`/`exigeRepetirValor`
   * cheguem `false` pela MESMA ausência (`mapearTerraformAdjuste`,
   * `ActionProposalCard.tsx`, deriva as duas do mesmo campo). Bloqueia
   * Confirmar e diz na tela por quê — o inverso de assumir seguro por
   * omissão. Default `false` para não quebrar os testes com dado completo. */
  dadosSegurancaAusentes?: boolean
  onConfirm?: () => void
  isConfirming?: boolean
  onDecline?: () => void
  isDeclining?: boolean
  /** O "Quero seguir" do pedido guardado (spec 2026-10-15 D3). */
  onRetomar?: () => void
  isRetomando?: boolean
  /** O pedido por trás do cartão (D1). `undefined` = proposta anterior ao
   * plan-antes-do-Confirmar (o cartão segue o fluxo antigo); `null` = a
   * proposta não virou pedido (ver `motivoBloqueio`). */
  pedido?: PedidoDoCartao | null
  /** A primeira leitura do pedido ainda não voltou. */
  pedidoCarregando?: boolean
  /** Fix round 1 (achado 1): há `pedido_id`, a primeira leitura já terminou
   * (`pedidoCarregando` é `false`), mas a leitura não voltou com dado —
   * `GET /change-requests/{id}` errou ou ainda está sem sucesso num
   * refetch. Fail-closed: SEM ISTO o cartão caía no ramo do fluxo antigo
   * (`pedido === undefined`) e mostrava Confirmar HABILITADO sobre o
   * `status` gravado uma vez na mensagem — sem saber se o pedido ainda
   * aceita confirmação. */
  pedidoIndisponivel?: boolean
  /** Por que a proposta não virou pedido (autorização, stage ocupado, tenant). */
  motivoBloqueio?: string | null
  /** `params.aviso_fila`: o pedido nasceu na fila (spec 2026-10-15 §5.2). Sem
   * Confirmar antes da vez — o clique daria 409 —, e a frase do lugar enquanto
   * o pedido não foi lido. */
  avisoFila?: string | null
}

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<TerraformAdjustCardStatus, string> = {
  pending: 'Pendente',
  // `executed` aqui é um registro GRAVADO UMA VEZ: significa que o clique de
  // confirmar não levantou exceção, e nada nunca o atualiza (não há UPDATE de
  // proposed_actions em lugar nenhum). O executor cria o pedido e para em
  // `confirmado` — sem plan, sem MR. Dizer "Executado" afirmava mudança que não
  // aconteceu; dizer "MR aberto" afirmava um artefato que ainda não existe e pode
  // nunca existir (plan_falhou, recusado_plano). O andamento real vive nas
  // mensagens que o worker escreve nesta conversa, não neste selo.
  executed: 'Pedido criado',
  failed: 'Falhou',
  declined: 'Recusado',
}

const STATUS_COLORS: Record<TerraformAdjustCardStatus, string> = {
  pending: 'yellow',
  // inovareTeal para contexto informacional (DESIGN_SYSTEM.md §2.3) — era
  // 'teal' (verde de concluído) para um estado de espera.
  executed: 'inovareTeal',
  failed: 'red',
  declined: 'gray',
}

function formatarMoeda(valor: number, moeda: string): string {
  const cifrao = moeda === 'USD' ? 'US$' : `${moeda} `
  return `${cifrao} ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ── Componente ─────────────────────────────────────────────────────────────

export function TerraformAdjustCard({
  stageChave,
  knob,
  alvo,
  diff,
  custo,
  avisos,
  irreversivel,
  exigeRepetirValor,
  status = 'pending',
  canConfirmPermission = true,
  dadosSegurancaAusentes = false,
  onConfirm,
  isConfirming = false,
  onDecline,
  isDeclining = false,
  onRetomar,
  isRetomando = false,
  pedido,
  pedidoCarregando = false,
  pedidoIndisponivel = false,
  motivoBloqueio,
  avisoFila = null,
}: TerraformAdjustCardProps) {
  const [valorDigitado, setValorDigitado] = useState('')

  const isPending = status === 'pending'
  // Valor-alvo para o portão de "repetir antes de confirmar": o `para` do
  // PRIMEIRO campo do diff — hoje só `lt.volume_size` é irreversível (Fase
  // 1a), e ele tem um único campo mudando por vez (spec §7.2).
  //
  // `valorAlvo !== ''` é a checagem que faltava (fix round 1): sem ela,
  // `diff` vazio deixava `valorAlvo` e `valorDigitado` os dois em `''`, e
  // `'' === ''` habilitava Confirmar sem NENHUMA entrada digitada — o
  // portão de repetir o valor virava um no-op silencioso.
  const valorAlvo = diff.length > 0 ? String(diff[0].para ?? '') : ''
  const repeticaoConfere =
    !exigeRepetirValor || (valorAlvo !== '' && valorDigitado.trim() === valorAlvo)
  // D1: com pedido, quem decide se o clique é permitido é o ESTADO do pedido
  // (`pode_confirmar`, recalculado no servidor a cada leitura) — não mais o
  // `status` gravado uma vez na mensagem. Sem `pedido` (fluxo antigo, proposta
  // anterior ao plan-antes-do-Confirmar) o gate segue sendo `isPending` — MAS
  // fail-closed quando há `pedido_id` e a leitura não voltou (achado 1): sem
  // saber o estado real, não afirmamos que o clique é permitido.
  const gatePendente = pedido ? pedido.pode_confirmar : !pedidoIndisponivel && isPending && !avisoFila
  const podeConfirmar =
    gatePendente && canConfirmPermission && repeticaoConfere && !dadosSegurancaAusentes

  // Regra §10.4 — nenhum número que não mude decisão: sem `deltaMensal` o
  // custo NUNCA aparece sozinho, só a ressalva (que diz por que não há
  // número: sem preço para o tipo/região, ou knob que não é de capacidade).
  //
  // Fix round 1 (review, Critical 1): a condição tem de exigir AS DUAS
  // coisas. Só `deltaMensal != null` deixava a ressalva com uma condição
  // INDEPENDENTE (linha da ressalva abaixo) — se o mapeador produzisse um
  // `deltaMensal` numérico sem `ressalva` (payload sem o campo, ou campo em
  // formato que o mapeador não reconhece), o card mostrava o delta em
  // dólar sem NENHUMA ressalva na tela. Sem a dupla, o par nunca se desfaz:
  // ou aparecem as duas coisas juntas, ou nenhuma.
  const temCusto = custo.deltaMensal != null && Boolean(custo.ressalva)

  return (
    <Card withBorder padding="sm" radius="md" style={{ maxWidth: 480 }}>
      {/* Cabeçalho: ícone SVG (16px, sem emoji) + título + status */}
      <Group gap="xs" mb="xs" wrap="nowrap" align="flex-start">
        <IconAdjustments size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <Stack gap={2} style={{ flex: 1 }}>
          <Text size="lg" fw={600} style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
            Ajustar infraestrutura
          </Text>
          <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
            <Dado>{alvo}</Dado> em <Dado>{stageChave}</Dado>
          </Text>
        </Stack>
        {!isPending && (
          <Badge size="xs" color={STATUS_COLORS[status]} variant="light">
            {STATUS_LABELS[status]}
          </Badge>
        )}
      </Group>

      {/* Diff: o que muda, campo a campo */}
      {diff.length > 0 && (
        <Stack gap={2} mb="xs">
          {diff.map((item) => (
            <Text
              key={item.campo}
              size="sm"
              style={{ fontFamily: '"Barlow", Arial, sans-serif' }}
            >
              {item.campo}: <Dado>{String(item.de)}</Dado> → <Dado>{String(item.para)}</Dado>
            </Text>
          ))}
        </Stack>
      )}

      {/* Custo — o número só aparece com a ressalva ao lado; sem número, só
          a ressalva (nunca "US$ —" nem qualquer substituto de valor). */}
      <Stack gap={2} mb="xs">
        {temCusto && (
          <Group gap={6} wrap="wrap">
            <Badge size="xs" color="yellow" variant="light">Custo</Badge>
            <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
              de <Dado>{formatarMoeda(custo.deMensal ?? 0, custo.moeda)}</Dado> para{' '}
              <Dado>{formatarMoeda(custo.paraMensal ?? 0, custo.moeda)}</Dado> — delta{' '}
              <Dado>{formatarMoeda(custo.deltaMensal as number, custo.moeda)}</Dado>/mês
            </Text>
          </Group>
        )}
        {custo.ressalva && (
          <Text size="xs" c="dimmed" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
            {custo.ressalva}
          </Text>
        )}
      </Stack>

      {/* Avisos — parada de máquina e afins, nomeando o alvo (nunca genérico) */}
      {avisos.length > 0 && (
        <Stack gap={2} mb="xs">
          {avisos.map((aviso) => (
            <Group key={aviso} gap={6} wrap="nowrap" align="flex-start">
              <Badge size="xs" color="yellow" variant="light" style={{ flexShrink: 0 }}>
                Atenção
              </Badge>
              <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                {aviso}
              </Text>
            </Group>
          ))}
        </Stack>
      )}

      {/* Irreversibilidade — badge + aviso fixo + portão de repetir o valor */}
      {irreversivel && (
        <Stack gap={6} mb="xs">
          <Group gap={6} wrap="nowrap" align="flex-start">
            <Badge size="xs" color="red" variant="light" style={{ flexShrink: 0 }}>
              Irreversível
            </Badge>
            <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
              Este ajuste não desfaz sozinho — confira o valor antes de confirmar.
            </Text>
          </Group>
          {exigeRepetirValor && (isPending || pedido?.pode_confirmar) && (
            <TextInput
              size="xs"
              label={
                <Text size="xs" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                  Digite <Dado>{valorAlvo}</Dado> para confirmar
                </Text>
              }
              placeholder={valorAlvo}
              value={valorDigitado}
              onChange={(e) => setValorDigitado(e.currentTarget.value)}
              styles={{ input: { fontFamily: '"Barlow", Arial, sans-serif' } }}
            />
          )}
        </Stack>
      )}

      {/* Fail-closed (fix round 1, Critical 2): o backend não informou se
          este knob é irreversível. NUNCA tratamos isso como "não é" —
          bloqueamos Confirmar e dizemos por quê, em vez de assumir seguro
          por omissão. */}
      {dadosSegurancaAusentes && (isPending || pedido?.pode_confirmar) && (
        <Group gap={6} mb="xs" wrap="nowrap" align="flex-start">
          <Badge size="xs" color="red" variant="light" style={{ flexShrink: 0 }}>
            Dados incompletos
          </Badge>
          <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
            Faltam dados de segurança para confirmar este ajuste — não é possível
            saber se ele é irreversível.
          </Text>
        </Group>
      )}

      <BlocoDoPedido
        pedido={pedido}
        pedidoCarregando={pedidoCarregando}
        pedidoIndisponivel={pedidoIndisponivel}
        motivoBloqueio={motivoBloqueio}
        isPending={isPending}
        podeConfirmar={podeConfirmar}
        onConfirm={onConfirm}
        isConfirming={isConfirming}
        onDecline={onDecline}
        isDeclining={isDeclining}
        onRetomar={onRetomar}
        isRetomando={isRetomando}
        avisoFila={avisoFila}
      />
    </Card>
  )
}


// ── O pedido por trás do cartão e os botões (D1) ───────────────────────────

/** O rótulo dos estados de fila (spec 2026-10-15 §5.8); `null` fora deles. */
export function rotuloDaFila(pedido: PedidoDoCartao): string | null {
  if (pedido.estado === 'na_fila') return 'Na fila'
  if (pedido.estado === 'aguardando_pedinte') {
    // O selo sai de quem vê, não do botão: com a base mudada o pedinte não
    // tem "Quero seguir", e a espera continua sendo dele (review da Onda 3, M2).
    const souOPedinte = pedido.sou_o_pedinte ?? pedido.pode_retomar
    return souOPedinte ? 'Esperando você' : 'Esperando quem pediu'
  }
  return null
}

export interface BlocoDoPedidoProps {
  pedido?: PedidoDoCartao | null
  pedidoCarregando: boolean
  pedidoIndisponivel: boolean
  motivoBloqueio?: string | null
  isPending: boolean
  /** O gate do clique, calculado pelo cartão (cada cartão tem o seu atrito). */
  podeConfirmar: boolean
  onConfirm?: () => void
  isConfirming: boolean
  onDecline?: () => void
  isDeclining: boolean
  /** O "Quero seguir" (spec 2026-10-15 D3): aparece só com `pode_retomar`. */
  onRetomar?: () => void
  isRetomando?: boolean
  /** `params.aviso_fila` (spec 2026-10-15 §5.2): nasceu na fila. */
  avisoFila?: string | null
}

/** O estado do pedido (plan, linha do tempo, execução, motivo, MR) e os
 * botões Confirmar/Recusar. Um só, para o cartão do ajuste por knob e o do
 * ajuste livre (spec 2026-10-08 D11): duas cópias envelheceriam separadas. */
export function BlocoDoPedido({
  pedido,
  pedidoCarregando,
  pedidoIndisponivel,
  motivoBloqueio,
  isPending,
  podeConfirmar,
  onConfirm,
  isConfirming,
  onDecline,
  isDeclining,
  onRetomar,
  isRetomando = false,
  avisoFila = null,
}: BlocoDoPedidoProps) {
  const rotuloFila = pedido ? rotuloDaFila(pedido) : null
  return (
    <>
      {/* D1: com pedido, quem decide os botões é o ESTADO do pedido, não o
          `status` gravado uma vez na mensagem. */}
      {pedidoCarregando && pedido === undefined && (
        <Text size="sm" c="dimmed" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
          Lendo o pedido…
        </Text>
      )}
      {/* Fix round 1 (achado 1): há pedido_id, mas a leitura não voltou com
          dado (erro, ou refetch ainda sem sucesso) — diz isso em vez de
          silenciosamente cair no fluxo antigo. A repergunta/poll de
          `useTerraformChangeRequest` já está tentando de novo sozinha. */}
      {!pedidoCarregando && pedidoIndisponivel && (
        <Text size="sm" c="dimmed" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
          Não deu para ler o estado do pedido — tentando de novo…
        </Text>
      )}
      {/* Nasceu na fila e o pedido ainda não foi lido: a frase do lugar. Lido,
          quem fala é o estado (rótulo "Na fila" e a posição). */}
      {avisoFila && !pedido && (
        <Group gap={6} wrap="nowrap" align="flex-start" mt="xs">
          <Badge size="xs" color="inovareTeal" variant="light" style={{ flexShrink: 0 }}>
            Na fila
          </Badge>
          <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>{avisoFila}</Text>
        </Group>
      )}
      {pedido === null && motivoBloqueio && (
        <Text size="sm" c="red" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
          {motivoBloqueio}
        </Text>
      )}
      {pedido && (
        <Stack gap={6} mt="xs">
          {/* Spec 2026-10-15 §5.8: a fila diz o lugar; o guardado diz o que
              falta e por quanto tempo (D3). */}
          {rotuloFila && (
            <Group gap={6} wrap="nowrap" align="flex-start">
              <Badge size="xs" color="inovareTeal" variant="light" style={{ flexShrink: 0 }}>
                {rotuloFila}
              </Badge>
              {pedido.estado === 'na_fila' && pedido.posicao != null && (
                <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                  <Dado>{`${pedido.posicao}º`}</Dado> na fila do stage <Dado>{pedido.stage_key}</Dado>
                </Text>
              )}
              {pedido.estado === 'aguardando_pedinte' && (
                <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                  {pedido.base_mudou
                    ? 'O código do stage mudou nos arquivos deste ajuste desde o pedido. Peça o ajuste de novo no chat: o pedido novo fica com o lugar deste.'
                    : 'A vez passou sem confirmação. O pedido fica guardado por 24 h, sem ocupar o stage.'}
                </Text>
              )}
            </Group>
          )}
          {pedido.linha_plan && (
            <Text size="sm" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {pedido.linha_plan}
            </Text>
          )}
          {Object.entries(pedido.acoes).map(([endereco, acoes]) => (
            <Text key={endereco} size="xs" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {acoes.join('/')} {endereco}
            </Text>
          ))}
          {/* consoleai#26352: o registro DNS que tira do ZTNA um host publicado. */}
          {(pedido.encobre_ztna ?? []).map((z) => (
            <Text key={z.endereco} size="xs" c="red" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
              Tira do ZTNA: {fraseDoEncobre(z)} — o nome passa a ir direto ao stage.
            </Text>
          ))}
          {/* Adendo I2 (spec 2026-10-15 §5.4): este pedido pausou a fila do stage. */}
          {pedido.pausa && <PausaDaFila pausa={pedido.pausa} />}
          <LinhaDoTempoDoPedido linha={pedido.linha_do_tempo} mrUrl={pedido.mr_url} />
          <DecisaoNoCartao pedido={pedido} />
          {/* A conclusão vem ANTES da execução (consoleai#24981): abaixo dela, a
              primeira linha do log ("Preparando o ambiente (init)") lia como
              título do erro. */}
          {pedido.terminal && pedido.motivo_terminal && (
            <MotivoDoPedido texto={pedido.motivo_terminal} />
          )}
          <PainelDeExecucao pedidoId={pedido.id} disponivel={pedido.andamento_disponivel}
                            acoes={pedido.acoes} totalDeMudancas={totalDoPlan(pedido.linha_plan)} />
          {/* D2/aceite 2: o portão do plan recusou por causa de endereços FORA
              do pedido — dizer QUAIS, não só que recusou. Terminal, então já
              não há Confirmar (ver `mostraConfirmar` abaixo). */}
          {pedido.estado === 'recusado_plano' && pedido.inesperadas.length > 0 && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                Mudanças fora do pedido:
              </Text>
              {pedido.inesperadas.map((endereco) => (
                <Text
                  key={endereco}
                  size="xs"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {endereco}
                </Text>
              ))}
            </Stack>
          )}
          {pedido.precisa_vincular_gitlab && (
            <Group gap={6}>
              <Text size="sm" style={{ fontFamily: '"Barlow", Arial, sans-serif' }}>
                Para o Confirmar mergear o MR em seu nome, conecte sua conta do GitLab.
              </Text>
              <Anchor component={Link} to="/perfil" size="sm">Conectar GitLab</Anchor>
            </Group>
          )}
          {pedido.mr_url && (
            <Anchor href={pedido.mr_url} target="_blank" rel="noreferrer" size="sm">
              Abrir o MR
            </Anchor>
          )}
        </Stack>
      )}
      {(() => {
        // Fix round 1: além de "tem pedido", há mais três casos que decidem
        // os botões de um jeito diferente do fluxo antigo (`isPending` puro).
        let mostraConfirmar: boolean
        let mostraRecusar: boolean
        if (pedido) {
          mostraConfirmar = pedido.pode_confirmar
          mostraRecusar = pedido.pode_recusar
        } else if (pedido === null) {
          // Achado 2: proposta bloqueada ANTES de virar pedido
          // (`motivoBloqueio`) — não há o que confirmar, mas Recusar segue
          // disponível para qualquer mensagem pendente, igual ao cartão
          // genérico (`ActionProposalCard`, ramo fora de `terraform_adjust`).
          mostraConfirmar = false
          mostraRecusar = isPending
        } else if (pedidoIndisponivel) {
          // Achado 1: há pedido_id, mas a leitura não voltou com dado.
          // Fail-closed no Confirmar; Recusar segue disponível porque
          // `decline_endpoint`/`cancelar_pedido_do_cartao` decide pelo
          // ESTADO gravado no banco, não pelo que esta tela conseguiu ler.
          mostraConfirmar = false
          mostraRecusar = true
        } else if (pedidoCarregando) {
          mostraConfirmar = false
          mostraRecusar = false
        } else {
          // Fluxo antigo: proposta sem `pedido_id` nenhum. O que nasceu na fila
          // não confirma antes da vez (o clique daria 409).
          mostraConfirmar = isPending && !avisoFila
          mostraRecusar = isPending
        }
        const mostraRetomar = Boolean(pedido?.pode_retomar && onRetomar)
        if (!mostraConfirmar && !mostraRecusar && !mostraRetomar) return null
        return (
          <Group gap="xs" mt="xs" wrap="nowrap">
            {mostraRetomar && (
              <Button size="xs" color="inovareTeal" variant="filled"
                      loading={isRetomando} onClick={onRetomar}>
                Quero seguir
              </Button>
            )}
            {mostraConfirmar && (
              <Button size="xs" color="inovareTeal" variant="filled"
                      disabled={!podeConfirmar} loading={isConfirming} onClick={onConfirm}>
                Confirmar
              </Button>
            )}
            {mostraRecusar && (
              <Button size="xs" color="red" variant="light"
                      loading={isDeclining} onClick={onDecline}>
                Recusar
              </Button>
            )}
          </Group>
        )
      })()}
    </>
  )
}
